import React, { useState } from 'react';
import { navigate, RouteComponentProps } from '@reach/router';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import FormTextField from 'components/formComponents/FormTextField';
import { PostResetPassword } from './resetPasswordApi';
import { ResetPasswordPost } from './models';
import {
  AppPath,
  PASSWORD_MATCH_POLICY_ERROR_MESSAGE,
  PASSWORD_MIN_LENGTH,
  PASSWORD_REGEX,
} from 'appConstants';
import { Spinner } from 'components/spinner/Spinner';
import ConfirmationBox from 'components/confirmationBox/ConfirmationBox';
import PasswordStrengthMeter from 'components/passwordStrengthMeter/PasswordStrengthMeter';

const SUCCESS_MESSAGE = {
  title: 'Congratulations',
  message: 'Your password has been reset.',
  buttonText: 'Sign in',
};

const ForgottenPasswordForm: React.FC<RouteComponentProps> = () => {
  const [submitError, setSubmitError] = useState('');
  const [formIsPosted, setFormIsPosted] = useState(false);

  const initialValues: ResetPasswordPost = {
    passwordResetToken: '',
    newPassword: '',
    confirmNewPassword: '',
  };

  const handleSubmitForm = async (values: ResetPasswordPost) => {
    setSubmitError('');

    const getURLToken = new URLSearchParams(window.location.search).get(
      'token'
    );
    const tokenVal = getURLToken === null ? '' : getURLToken;

    values.passwordResetToken = encodeURIComponent(tokenVal);

    PostResetPassword(values).then((response) => {
      if (!response.errors) {
        setFormIsPosted(true);
        setSubmitError('');
      } else {
        setFormIsPosted(false);
        setSubmitError('Reset failed, please try again');
      }
    });
  };

  const validationSchema = Yup.object({
    newPassword: Yup.string()
      .required('You must provide a password')
      .min(PASSWORD_MIN_LENGTH, PASSWORD_MATCH_POLICY_ERROR_MESSAGE)
      .matches(PASSWORD_REGEX, PASSWORD_MATCH_POLICY_ERROR_MESSAGE),
    confirmNewPassword: Yup.string()
      .required('You must confirm your new password')
      .oneOf([Yup.ref('newPassword'), null], 'Passwords must match'),
  });

  return (
    <div className="Form">
      {!formIsPosted ? (
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmitForm}
          validationSchema={validationSchema}
        >
          {({ isSubmitting, values }) => (
            <Form>
              <h1>Reset Password</h1>
              <p className="SubTitle">Enter your new password below</p>
              <div className="UserCredentialsSection">
                <FormTextField
                  field={'newPassword'}
                  label={'Password'}
                  type={'password'}
                  required={true}
                />
                <PasswordStrengthMeter password={values.newPassword} />
                <FormTextField
                  field={'confirmNewPassword'}
                  label={'Confirm Password'}
                  type={'password'}
                  required={true}
                />
              </div>
              {submitError && <div className="GeneralError">{submitError}</div>}
              <button
                className="Btn BtnOrange"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <Spinner color={'#ffffff'} />
                ) : (
                  'Request Password'
                )}
              </button>
            </Form>
          )}
        </Formik>
      ) : (
        <div className="Success">
          <div className="ConfirmationBoxContainer">
            <ConfirmationBox
              {...SUCCESS_MESSAGE}
              buttonOnClick={() => navigate(AppPath.SIGN_IN)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ForgottenPasswordForm;
